import React from 'react';
import { Stack, Text, Button, Divider } from '@moda/om';
import { hrefFor } from '../../routers';
import { formatPrice } from '../../lib/formatPrice';
import { useCart, useCartItems } from '../../hooks/useCart';
import {
  CheckoutAmazonPayButton,
  CheckoutApplePayButton,
  CheckoutPayPalButton
} from '../CheckoutPaymentSources';
import { AffirmPromoMessage } from '../AffirmPromoMessage';
import { RewardSummary } from '../Rewards';
import { GiftCode } from '../GiftCode';
import { PaymentSummaryRow } from './PaymentSummaryRow';
import { PaymentSummaryTaxRow } from './PaymentSummaryTaxRow';

import './ShoppingBagPaymentSummary.scss';

export const ShoppingBagPaymentSummary: React.FC = () => {
  const {
    cart: {
      attributes: {
        paymentSummary: {
          subtotal,
          total,
          estimatedVat,
          estimatedGst,
          estimatedDuty,
          estimatedSalesTax,
          estimatedShipping,
          siteCredit,
          promotionDiscount,
          payToday,
          payLater,
          vatLabel,
          salesLabel,
          gstLabel,
          dutyLabel,
          isShippingPromotionApplied
        }
      }
    }
  } = useCart();
  const { unavailableItems, invalidItems, personalizationRequiredItems } = useCartItems();

  return (
    <Stack space={4} className="ShoppingBagPaymentSummary">
      <Text treatment="h4" family="serif">
        Payment Summary
      </Text>
      <Divider />
      <Stack space={3}>
        <PaymentSummaryRow
          label="Order Subtotal"
          value={formatPrice(subtotal.currency, subtotal.price)}
        />
        {estimatedDuty && (
          <PaymentSummaryTaxRow
            label={dutyLabel}
            value={formatPrice(estimatedDuty.currency, estimatedDuty.price)}
          />
        )}
        {estimatedVat && (
          <PaymentSummaryTaxRow
            label={vatLabel}
            value={formatPrice(estimatedVat.currency, estimatedVat.price)}
          />
        )}
        {estimatedGst && (
          <PaymentSummaryTaxRow
            label={gstLabel}
            value={formatPrice(estimatedGst.currency, estimatedGst.price)}
          />
        )}

        {estimatedSalesTax && (
          <PaymentSummaryTaxRow
            label={salesLabel}
            value={formatPrice(estimatedSalesTax.currency, estimatedSalesTax.price)}
          />
        )}

        <PaymentSummaryRow
          label="Est. Shipping"
          tooltip="We estimate shipping fees using your location. You will be able to change your shipping method before completing your order."
          value={
            isShippingPromotionApplied
              ? 'Free'
              : formatPrice(estimatedShipping.currency, estimatedShipping.price)
          }
        />

        <RewardSummary theme="simple" format="negative" />

        <GiftCode theme="simple" promotionDiscount={promotionDiscount} />

        {siteCredit.price > 0 && (
          <PaymentSummaryRow
            label="Site Credit"
            value={`-${formatPrice(siteCredit.currency, siteCredit.price)}`}
          />
        )}
      </Stack>
      <div className="ShoppingBagPaymentSummary__total">
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, total.price)} />
      </div>
      <Stack space={3}>
        <Stack space={1} className="ShoppingBagPaymentSummary__pay-today">
          <div className="ShoppingBagPaymentSummary__pay-today-title" data-testid="Pay Today">
            <PaymentSummaryRow
              label="Est. Pay Today"
              value={formatPrice(payToday.currency, payToday.price)}
            />
          </div>
          {Number(payLater.price) === 0 && (
            <div className="ShoppingBagPaymentSummary__affirm-promo">
              <AffirmPromoMessage pageType="cart" price={Number(payToday.price)} />
            </div>
          )}
        </Stack>
        {Number(payLater.price) > 0 && (
          <PaymentSummaryRow
            label="Est. Remaining Balance"
            tooltip="Preorder items are made especially for you and require a 50% deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
            tooltipAnchor="bottomCenter"
            value={formatPrice(payLater.currency, payLater.price)}
          />
        )}
      </Stack>
      <Button
        to={hrefFor.CheckoutPage()}
        disabled={
          unavailableItems.length > 0 ||
          invalidItems.length > 0 ||
          personalizationRequiredItems.length > 0
        }
        data-testid="shoppingBagCheckoutButton"
      >
        Checkout
      </Button>
      <div className="ShoppingBagPaymentSummary__divider">
        <Text className="ShoppingBagPaymentSummary__divider-text">OR</Text>
      </div>
      <Text className="ShoppingBagPaymentSummary__label">
        Skip the forms and use your saved information
      </Text>
      <div className="ShoppingBagPaymentSummary__payment-methods">
        <CheckoutPayPalButton />
        {payToday.price > 0 && <CheckoutApplePayButton />}
        <CheckoutAmazonPayButton />
      </div>
    </Stack>
  );
};
